import React, { useEffect, useRef, useState } from "react";

import { Box, MenuItem, TextField } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
interface ViewerProps {
  blob: Blob | null;
}
const MIN_SCALE = 1;
const SCALE_FACTOR = 1.0416;
export const PDFViewer: React.FC<ViewerProps> = ({ blob }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(MIN_SCALE);
  const [error, setError] = useState<string | null>(null);
  const documentRef = useRef<HTMLElement>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setError(null);
    console.log("PDF loaded successfully");
  }
  function onDocumentLoadError(error: any): void {
    setError(`Failed to load PDF file. Please check the file path or format.`);
    console.error("PDF load error", error);
  }
  const handleScroll = () => {
    if (documentRef.current) {
      const currentPage =
        Math.floor(documentRef.current.scrollTop / (scale * SCALE_FACTOR * 540)) + 1;
      if (currentPage <= numPages) setPageNumber(currentPage);
    }
  };
  const handleChangePage = (currentPage: number) => {
    if (documentRef.current) {
      documentRef.current.scrollTo({
        top: (currentPage - 1) * (scale * SCALE_FACTOR * 550),
      });
    }
  };
  useEffect(() => {
    const doc = documentRef.current;
    if (doc) {
      doc.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (doc) {
        doc.removeEventListener("scroll", handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, numPages, scale]);

  return (
    <div className="flex flow-column" style={{ maxHeight: "75vh" }}>
      <div className="document-view-pdf-toolbar flex space-between ">
        <div className="flex align-items-center" style={{ columnGap: "10px" }}>
          <TextField
            type="number"
            slotProps={{ htmlInput: { min: 1, max: numPages } }}
            value={pageNumber}
            onChange={(e) => {
              handleChangePage(+e.target.value);
            }}
            size="small"
            className="document-view-pdf-page-count"
          />
          <span>of</span>
          <span>{numPages}</span>
        </div>
        <div className="flex align-items-center">
          <TextField
            select
            value={scale * 100}
            onChange={(e) => {
              setScale(+e.target.value / 100);
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "white",
              marginTop: "0",
              marginBottom: "0",
            }}
          >
            {[100, 80, 50, 30, 20].map((option) => (
              <MenuItem key={option} value={option}>
                {`${option}%`}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>
      <Box className="document-view-pdf-file" ref={documentRef}>
        <Box data-testid="pdf-box" sx={{ width: "100%" }}>
          {error && <div style={{ color: "red", padding: "1em" }}>{error}</div>}
          {!error && blob && (
            <Document
              file={blob}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            >
              {Array.from(new Array(numPages), (_, index) => {
                return (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    scale={scale}
                    height={1000}
                    width={1000}
                  />
                );
              })}
            </Document>
          )}
        </Box>
      </Box>
    </div>
  );
};
